import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { registerLocaleData, DatePipe, CommonModule } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import en from '@angular/common/locales/en';

import { AdminAngularAntdModule } from './modules/angular-antd.module';
import { AppRoutingModule } from './app.routes';
import { ErrorInterceptor } from './interceptors/error.interceptor';

import { AppComponent } from './app.component';

import { AuthGuard } from './guards/auth.guard';
import { LoginGuard } from './guards/login.guard';

import { AuthService } from './services/auth/auth.service';
import { APIService } from './services/api/api.service';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { reducers, effects } from './+store/app.states';
import { CommonFacade } from './+store/common/common.facade';
import { OrdersFacade } from './+store/order/order.facade';
import { InvoiceFacade } from './+store/invoice/invoice.facade';
import { DrawerStateFacade } from './+store/drawer-state/drawer-state.facade';
import { CustomerFacade } from './+store/customer/customer.facade';
import { ConsultationFacade } from './+store/consultation/consultation.facade';
import { PrescriptionFacade } from './+store/prescription/prescription.facade';
import { DrugFacade } from './+store/drug/drug.facade';
import { NoteFacade } from './+store/note/note.facade';
import { MessageFacade } from './+store/message/message.facade';
import { FileFacade } from './+store/file/file.facade';
import { AuthFacade } from './+store/auth/auth.facade';
import { NotificationFacade } from './+store/notification/notification.facade';
import { AppFacade } from './+store/app/app.facade';
import { AppointmentFacade } from './+store/appointment/appointment.facade';
import { SubscriptionsFacade } from './+store/subscriptions/subscriptions.facade';
import { LabReqFacade } from './+store/lab-req/lab-req.facade';
import { LabResultsFacade } from './+store/lab-results/lab-results.facade';
import { CannedMessagesFacade } from './+store/canned-messages/canned-messages.facade';

import { AdminUserFacade } from './+store/admin-user/admin-user.facade';
import { OrganizationInterceptor } from './interceptors/organization.interceptor';
import { NgxImageCompressService } from 'ngx-image-compress';
import { ChatService } from './+store/chat/chat.service';
import { ChatFacade } from './+store/chat/chat.facade';
import {
  NbCardModule,
  NbFocusMonitor,
  NbStatusService,
  NbThemeModule
} from '@nebular/theme';
import { CRMService } from './services/api/crm.service';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { LookupService } from '@care-portals/ui';
import { OverlayModule } from '@angular/cdk/overlay';
import { SessionOverlayComponent } from './components/session-overlay/session-overlay.component';
import { ProductFacade } from './+store/product/product.facade';

registerLocaleData(en);

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AdminAngularAntdModule,
    SessionOverlayComponent,
    StoreModule.forRoot(reducers, {
      metaReducers: !environment.production ? [] : [],
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true
      }
    }),
    EffectsModule.forRoot(effects),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    NbCardModule,
    NbThemeModule,
    NzTypographyModule,
    NbThemeModule,
    OverlayModule
  ],
  providers: [
    AuthService,
    APIService,
    ChatService,
    AuthGuard,
    LoginGuard,
    DatePipe,
    NbStatusService,
    NbFocusMonitor,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OrganizationInterceptor,
      multi: true
    },
    CommonFacade,
    AuthFacade,
    OrdersFacade,
    InvoiceFacade,
    DrawerStateFacade,
    CustomerFacade,
    ConsultationFacade,
    PrescriptionFacade,
    DrugFacade,
    NoteFacade,
    MessageFacade,
    CannedMessagesFacade,
    FileFacade,
    LabReqFacade,
    LabResultsFacade,
    NotificationFacade,
    AppFacade,
    AppointmentFacade,
    ChatFacade,
    AdminUserFacade,
    SubscriptionsFacade,
    NgxImageCompressService,
    CRMService,
    ProductFacade,
    [
      LookupService,
      { provide: 'lookupEndpoint', useValue: environment.crmApi.baseUrl },
      { provide: 'apiEndpoint', useValue: environment.api.baseUrl }
    ]
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
